import React from 'react';
import { createPortal } from 'react-dom';
import {
  filterMembersForMentionQuery,
  type MentionMember,
  mentionLabel,
} from '../../utils/notesMentions';
import { UI_Z_CLASS } from '../../utils/uiZIndex';

type Props = {
  open: boolean;
  anchorRect: DOMRect | null;
  members: MentionMember[];
  query: string;
  onSelect: (member: MentionMember) => void;
  onClose: () => void;
};

export function NotesMentionPicker({
  open,
  anchorRect,
  members,
  query,
  onSelect,
  onClose,
}: Props) {
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const listRef = React.useRef<HTMLDivElement>(null);

  const filtered = React.useMemo(
    () => filterMembersForMentionQuery(members, query),
    [members, query],
  );

  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [query, open, filtered.length]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        if (!filtered.length) return;
        setHighlightedIndex((i) => (i + 1) % filtered.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        if (!filtered.length) return;
        setHighlightedIndex((i) => (i - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (!filtered.length) return;
        e.preventDefault();
        e.stopPropagation();
        const pick = filtered[highlightedIndex] ?? filtered[0];
        if (pick) onSelect(pick);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, filtered, highlightedIndex, onClose, onSelect]);

  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('[data-mention-option-active="true"]');
    if (active instanceof HTMLElement) {
      active.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, open]);

  if (!open || !anchorRect) return null;

  const top = anchorRect.bottom + 6;
  const left = Math.max(8, anchorRect.left);

  return createPortal(
    <div
      ref={listRef}
      className={`fixed ${UI_Z_CLASS.portalPanel} w-64 max-h-56 overflow-y-auto rounded-lg border border-app-border bg-app-surface shadow-xl py-1`}
      style={{ left, top }}
      role="listbox"
      aria-label="Nutzer erwähnen"
      onMouseDown={(e) => e.preventDefault()}
    >
      {filtered.length === 0 ? (
        <div className="px-3 py-2 text-sm text-app-text-subtle">Keine Nutzer gefunden</div>
      ) : (
        filtered.map((member, index) => {
          const active = index === highlightedIndex;
          return (
            <button
              key={member.user_id}
              type="button"
              role="option"
              aria-selected={active}
              data-mention-option-active={active ? 'true' : undefined}
              className={`w-full text-left px-3 py-2 text-sm ${
                active ? 'bg-blue-50 text-pipedrive-blue' : 'hover:bg-app-hover'
              }`}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => onSelect(member)}
            >
              <span className={`font-medium ${active ? 'text-pipedrive-blue' : 'text-app-text'}`}>
                @{mentionLabel(member)}
              </span>
              {member.display_name && member.display_name !== mentionLabel(member) ? (
                <span className="block text-xs text-app-text-subtle truncate">{member.display_name}</span>
              ) : null}
            </button>
          );
        })
      )}
    </div>,
    document.body,
  );
}
